import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getJournalBySlug,
  getSnapshotQuestions,
  saveSnapshotResponses,
  getUserProgress,
  createUserProgress,
} from "../services/moneyMagicService";
import "./MoneyMagicSnapshot.css";

const getSessionFromStorage = () => {
  try {
    const storageKey = Object.keys(localStorage).find(
      (k) => k.includes("sb-") && k.includes("-auth-token")
    );
    if (!storageKey) return null;
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { userId: parsed?.user?.id };
  } catch {
    return null;
  }
};

function UntangleSnapshot() {
  const navigate = useNavigate();
  const { snapshotType } = useParams();
  const isAfter = snapshotType === "after";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [journalId, setJournalId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [totalDays, setTotalDays] = useState(30);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      if (snapshotType !== "before" && snapshotType !== "after") {
        navigate("/journal/the-untangle-journal", { replace: true });
        return;
      }

      const session = getSessionFromStorage();
      if (!session?.userId) {
        navigate("/login", { replace: true });
        return;
      }

      const journalData = await getJournalBySlug("the-untangle-journal");
      if (!alive) return;
      if (!journalData) {
        navigate("/journal/the-untangle-journal", { replace: true });
        return;
      }

      const progress = await getUserProgress(session.userId, journalData.id);
      if (!alive) return;

      if (!isAfter) {
        if (progress) {
          navigate(`/journal/the-untangle-journal/day/${progress.currentDay}`, { replace: true });
          return;
        }
      } else {
        if (!progress || !progress.completedAt) {
          navigate("/journal/the-untangle-journal", { replace: true });
          return;
        }
      }

      const qs = await getSnapshotQuestions(journalData.id, snapshotType);
      if (!alive) return;

      // No snapshot questions for this journal — skip the snapshot step entirely
      if (!qs.length) {
        if (!isAfter) {
          let prog = await createUserProgress(session.userId, journalData.id);
          if (!prog) prog = await getUserProgress(session.userId, journalData.id);
          navigate(`/journal/the-untangle-journal/day/${prog?.currentDay ?? 1}`, { replace: true });
        } else {
          navigate("/journal/the-untangle-journal/complete", { replace: true });
        }
        return;
      }

      setUserId(session.userId);
      setJournalId(journalData.id);
      setTotalDays(journalData.total_days ?? 30);
      setQuestions(qs);
      const initial = {};
      qs.forEach((q) => { initial[q.id] = ""; });
      setResponses(initial);
      setLoading(false);
    };

    load();

    return () => { alive = false; };
  }, [navigate, snapshotType, isAfter]);

  const handleChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);

    try {
      const payload = questions.map((q) => ({
        questionId: q.id,
        response: responses[q.id] ?? "",
      }));

      const saved = await saveSnapshotResponses(userId, journalId, snapshotType, payload);
      if (!saved) {
        setError("We couldn't save your snapshot. Please try again.");
        setSubmitting(false);
        return;
      }

      if (!isAfter) {
        let progress = await createUserProgress(userId, journalId);
        if (!progress) {
          progress = await getUserProgress(userId, journalId);
        }
        const startDay = progress?.currentDay ?? 1;
        navigate(`/journal/the-untangle-journal/day/${startDay}`, { replace: true });
      } else {
        navigate("/journal/the-untangle-journal/complete", { replace: true });
      }
    } catch (err) {
      console.error("[UntangleSnapshot] submit failed:", err);
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mms-gate">
        <p className="mms-gate-text">Preparing your snapshot…</p>
      </div>
    );
  }

  return (
    <div className="mms-page">
      <div className="mms-header">
        <p className="mms-eyebrow">Edge &amp; Altar · The Untangle Journal</p>
        <div className="mms-badge">{isAfter ? "After" : "Before"}</div>

        {isAfter ? (
          <>
            <h1 className="mms-title">Your Transformation</h1>
            <p className="mms-subtitle">
              30 days ago, you answered these questions. Answer them again now
              and see how far you have come.
            </p>
          </>
        ) : (
          <>
            <h1 className="mms-title">Your Starting Point</h1>
            <p className="mms-subtitle">
              Answer honestly — there are no right responses here. These words are
              for you alone. On Day 30, you'll return to this page and witness your
              own shift.
            </p>
          </>
        )}
      </div>

      <div className="mms-form">
        {questions.map((q, i) => (
          <div key={q.id} className="mms-question">
            <label className="mms-question-label" htmlFor={`q-${q.id}`}>
              <span className="mms-question-number">{i + 1}</span>
              {q.questionText}
            </label>
            <textarea
              id={`q-${q.id}`}
              className="mms-textarea"
              value={responses[q.id] ?? ""}
              onChange={(e) => handleChange(q.id, e.target.value)}
              rows={4}
              placeholder="Write freely…"
            />
          </div>
        ))}

        {error && <p className="mms-error">{error}</p>}

        <div className="mms-submit-row">
          <p className="mms-submit-note">
            🔒 Your responses are private and protected. Only you can see what you write here.
          </p>
          <button
            className="mms-submit-btn"
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting
              ? "Saving…"
              : isAfter
              ? "See My Transformation →"
              : "Begin Day 1 →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UntangleSnapshot;
